import React from 'react';
import useDraggable from '../hooks/useDraggable';
import useTrackPageView from '../hooks/useTrackPageView';
import { useTranslation } from '../i18n/LanguageContext';

const Section = ({ title, children }) => (
  <div className="sh-section">
    <h3 className="sh-section-title">{title}</h3>
    {children}
  </div>
);

const Param = ({ name, unit, recommended, recommendedLabel, children }) => (
  <div className="sh-param">
    <div className="sh-param-header">
      <span className="sh-param-name">{name}</span>
      {unit && <span className="sh-param-unit">{unit}</span>}
      {recommended && <span className="sh-param-rec">{recommendedLabel} {recommended}</span>}
    </div>
    <div className="sh-param-body">{children}</div>
  </div>
);

const Example = ({ label, children }) => (
  <div className="sh-example">
    <span className="sh-example-label">{label}</span>
    <span className="sh-example-text">{children}</span>
  </div>
);

const Code = ({ children }) => (
  <p style={{ fontFamily: 'monospace', background: 'var(--surface2)', padding: '6px 10px', borderRadius: 4, marginTop: 4, fontSize: 12, lineHeight: 1.7 }}>
    {children}
  </p>
);

const Note = ({ children }) => (
  <div style={{ background: 'rgba(13,148,136,.08)', border: '1px solid rgba(13,148,136,.25)', borderRadius: 7, padding: '8px 12px', fontSize: 12, color: 'var(--accent)', marginTop: 8 }}>
    {children}
  </div>
);

const Timeline = ({ steps }) => (
  <div className="sh-timeline">
    {steps.map((s, i) => (
      <div key={i} className="sh-timeline-step">
        <div className="sh-timeline-dot" style={{ background: s.color || 'var(--accent)' }} />
        <div className="sh-timeline-content">
          <div className="sh-timeline-label" style={{ color: s.color || 'var(--accent)' }}>{s.label}</div>
          <div className="sh-timeline-text">{s.text}</div>
        </div>
      </div>
    ))}
  </div>
);

export default function RiskHelp({ onClose }) {
  const { t, lang } = useTranslation();
  const isEn = lang === 'en';
  const rec = isEn ? 'Recommended:' : 'Önerilen:';
  const { offset: dragOffset, onMouseDown: handleHeaderMouseDown } = useDraggable();
  useTrackPageView('Ayarlar - Risk Yonetimi Rehberi');
  return (
    <div className="sh-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="sh-modal" style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}>

        <div className="sh-header" onMouseDown={handleHeaderMouseDown}>
          <div>
            <h2 className="sh-title">{isEn ? 'Risk Management Guide' : 'Risk Yönetimi Rehberi'}</h2>
            <p className="sh-subtitle">{isEn ? 'SL calculation and strategy comparison' : 'SL hesaplaması ve strateji karşılaştırması'}</p>
          </div>
          <button className="sh-close" onClick={onClose}>✕</button>
        </div>

        <div className="sh-body">

          {/* SL Sabit Kalma Kuralı */}
          <Section title={isEn ? '🔒 Fixed Stop Loss — Basic Rule' : '🔒 Sabit Stop Loss — Temel Kural'}>
            {isEn ? (
              <p className="sh-text">
                <strong>In every trade — bot-opened or manual — the SL is fixed at open and never changes afterward.</strong>
                No matter which direction the price moves, whether it's in profit or loss, the SL never moves based on any parameter.
              </p>
            ) : (
              <p className="sh-text">
                <strong>Bot veya manuel olarak açılan tüm işlemlerde SL açılışta sabitlenir ve sonradan değişmez.</strong>
                Fiyat ne yönde giderse gitsin, kâr veya zarar oluşsa da SL hiçbir parametreye bağlı olarak hareket etmez.
              </p>
            )}
            <Note>
              {isEn
                ? 'The maximum loss is known upfront: whatever your SL level is, that\'s exactly the risk on the trade, and it never changes.'
                : 'Maksimum zarar baştan bellidir: SL seviyen neyse işlemde o kadar risk vardır ve bu hiç değişmez.'}
            </Note>
            <Note>
              {isEn
                ? <strong>Exception — RSI+SMA / SMA-50:</strong>
                : <strong>İstisna — RSI+SMA / SMA-50:</strong>}{' '}
              {isEn
                ? 'these 2 strategies have NO ATR safety-net fallback — if SL Percentage is left at 0/blank, the position has no fixed price protection at all (closes only via a structural exit). See the "TP Percentage" section below.'
                : "bu 2 stratejide ATR yedek-sigorta fallback'ı YOKTUR — SL Yüzdesi 0/boş bırakılırsa pozisyonun hiçbir sabit fiyat koruması olmaz (yalnızca yapısal çıkışla kapanır). Aşağıdaki \"TP Yüzdesi\" bölümüne bakın."}
            </Note>
          </Section>

          {/* ATR Nedir */}
          <Section title={isEn ? '📐 What Is ATR? — Basic Concept' : '📐 ATR Nedir? — Temel Kavram'}>
            {isEn ? (
              <p className="sh-text">
                <strong>ATR (Average True Range)</strong> measures an asset's average price fluctuation over a given period.
                SL calculations are based on ATR — not a fixed USDT amount.
              </p>
            ) : (
              <p className="sh-text">
                <strong>ATR (Average True Range)</strong>, bir varlığın belirli bir süredeki ortalama fiyat dalgalanmasını ölçer.
                SL hesaplamalarının temeli ATR'dir — sabit USDT değil.
              </p>
            )}
            <p className="sh-text">
              {isEn
                ? 'This way the system adapts automatically: a wide SL in volatile markets, a narrow one in calm markets.'
                : 'Bu sayede sistem otomatik olarak adapte olur: volatil piyasada geniş, sakin piyasada dar bir SL seviyesi kullanılır.'}
            </p>

            <Param name={isEn ? 'ATR Period' : 'ATR Periyodu'} unit="bar" recommended="14" recommendedLabel={rec}>
              <p>{isEn ? 'Determines how many candles back are used to calculate ATR.' : 'ATR hesaplanırken kaç mum geriye bakılacağını belirler.'}</p>
              <Example label={isEn ? 'Suggestion' : 'Öneri'}>{isEn ? '14, the universal standard.' : '14, evrensel standart.'}</Example>
            </Param>
          </Section>

          {/* SL ATR Modu */}
          <Section title={isEn ? '🛡 Stop Loss — ATR Mode' : '🛡 Stop Loss — ATR Modu'}>
            <p className="sh-text">
              <strong>SL% = 0</strong> {isEn ? 'is when ATR mode is active.' : 'iken ATR modu aktiftir.'}
            </p>

            <Param name={isEn ? 'SL Multiplier' : 'SL Çarpanı'} unit="× ATR" recommended="1.2" recommendedLabel={rec}>
              <Code>
                {isEn ? 'Long  SL = Entry − (SL Multiplier × ATR)' : 'Long  SL = Giriş − (SL Çarpanı × ATR)'}{'\n'}
                {isEn ? 'Short SL = Entry + (SL Multiplier × ATR)' : 'Short SL = Giriş + (SL Çarpanı × ATR)'}
              </Code>
              <Example label={isEn ? 'Example' : 'Örnek'}>{isEn ? 'BTC entry 60,000, ATR 800, SL=1.2 → Long SL = 60,000 − 960 = 59,040 USDT' : 'BTC girişi 60.000, ATR 800, SL=1.2 → Long SL = 60.000 − 960 = 59.040 USDT'}</Example>
            </Param>
          </Section>

          {/* % Modu */}
          <Section title={isEn ? '💹 SL Percentage — % Mode' : '💹 SL Yüzdesi — % Modu'}>
            <p className="sh-text">
              <strong>{isEn ? 'When SL% is set above zero' : 'SL% sıfırdan büyük ayarlandığında'}</strong> {isEn ? 'a percentage of the entry price is used instead of ATR.' : 'ATR yerine giriş fiyatının yüzdesi kullanılır.'}
            </p>
            <Note>{isEn ? 'If SL% > 0, the SL Multiplier (ATR) is disabled.' : 'SL% > 0 ise SL Çarpanı (ATR) devre dışı.'}</Note>

            <Param name={isEn ? 'SL Percentage' : 'SL Yüzdesi'} unit="%" recommended={isEn ? '0 (ATR mode)' : '0 (ATR modu)'} recommendedLabel={rec}>
              <Code>
                {isEn ? 'Long  SL = Entry × (1 − SL% / 100)' : 'Long  SL = Giriş × (1 − SL% / 100)'}{'\n'}
                {isEn ? 'Short SL = Entry × (1 + SL% / 100)' : 'Short SL = Giriş × (1 + SL% / 100)'}
              </Code>
              <Example label={isEn ? 'Example' : 'Örnek'}>{isEn ? 'Entry = 1.0207, SL% = 0.5 → Long SL = 1.0156' : 'Giriş = 1.0207, SL% = 0.5 → Long SL = 1.0156'}</Example>
            </Param>
          </Section>

          {/* TP Yüzdesi — yalnızca RSI+SMA / SMA-50 */}
          <Section title={isEn ? '💰 TP Percentage — RSI+SMA / SMA-50' : '💰 TP Yüzdesi — RSI+SMA / SMA-50'}>
            <p className="sh-text">
              {isEn
                ? <>Only available for the <strong>RSI+SMA</strong> and <strong>SMA-50</strong> strategies — the other
                strategies have no TP field. Unlike SL, there is no ATR mode for TP here: <strong>0 or blank always
                means TP is fully off</strong> (the position then closes only via SL or a structural exit — the SMA
                flip / Dead Zone return).</>
                : <>Yalnızca <strong>RSI+SMA</strong> ve <strong>SMA-50</strong> stratejilerinde vardır — diğer
                stratejilerde TP alanı yoktur. SL'nin aksine burada bir ATR modu YOKTUR: <strong>0 veya boş her zaman
                TP'nin tamamen kapalı olduğu</strong> anlamına gelir (pozisyon o zaman yalnızca SL veya yapısal çıkışla —
                SMA flip / Ölü Bölge dönüşüyle — kapanır).</>}
            </p>
            <Note>
              {isEn
                ? "These 2 strategies also have NO ATR fallback for SL — if BOTH TP% and SL% are left at 0/blank, the position has no fixed price protection at all."
                : "Bu 2 stratejide SL için de ATR fallback'ı YOKTUR — hem TP% hem SL% 0/boş bırakılırsa pozisyonun hiçbir sabit fiyat koruması olmaz."}
            </Note>

            <Param name={isEn ? 'TP Percentage' : 'TP Yüzdesi'} unit="%" recommended={isEn ? '0 (off)' : '0 (kapalı)'} recommendedLabel={rec}>
              <Code>
                {isEn ? 'Long  TP = Entry × (1 + TP% / 100)' : 'Long  TP = Giriş × (1 + TP% / 100)'}{'\n'}
                {isEn ? 'Short TP = Entry × (1 − TP% / 100)' : 'Short TP = Giriş × (1 − TP% / 100)'}
              </Code>
              <Example label={isEn ? 'Example' : 'Örnek'}>{isEn ? 'Entry = 1.0207, TP% = 1 → Long TP = 1.0309' : 'Giriş = 1.0207, TP% = 1 → Long TP = 1.0309'}</Example>
            </Param>
          </Section>

          {/* ADX Eşiği */}
          <Section title={isEn ? '📊 ADX Threshold — Optional Filter' : '📊 ADX Eşiği — Opsiyonel Filtre'}>
            <p className="sh-text">
              {isEn ? (
                <>The HMA and ADX lines are <strong>off</strong> by default on the chart — they appear if the "HMA Indicator" /
                "ADX Indicator" checkboxes are checked under the Signal tab. Whether they're factored into the trading decision
                (independent of visibility) is determined by the <strong>ADX Threshold</strong> parameter.</>
              ) : (
                <>HMA ve ADX çizgileri grafikte varsayılan olarak <strong>kapalıdır</strong> — Sinyal sekmesindeki "HMA İndikatör" / "ADX İndikatör" kutucukları işaretlenirse görünür. Ticaret kararına dahil edilip edilmeyeceğini ise (görünürlükten bağımsız olarak) <strong>ADX Eşiği</strong> parametresi belirler.</>
              )}
            </p>

            <Param name={isEn ? 'ADX Threshold' : 'ADX Eşiği'} recommended={isEn ? '0 (disabled)' : '0 (devre dışı)'} recommendedLabel={rec}>
              {isEn ? (
                <>
                  <p><strong>When set to 0</strong>, the ADX filter is completely disabled — only the HMA direction is needed.</p>
                  <p style={{ marginTop: 6 }}><strong>When set above 0</strong> (e.g. 23), both the HMA direction <em>and</em> ADX ≥ threshold are required. The same threshold also applies to the trend-reversal close decision.</p>
                  <Example label="Example">ADX Threshold = 23.8 → if HMA is up but ADX is 20, the signal is skipped</Example>
                </>
              ) : (
                <>
                  <p><strong>0 ayarlandığında</strong> ADX filtresi tamamen devre dışıdır — yalnızca HMA yönü yeterlidir.</p>
                  <p style={{ marginTop: 6 }}><strong>0'dan büyük ayarlandığında</strong> (örn. 23) HMA yönü <em>ve</em> ADX ≥ eşik koşulunun ikisi birden aranır. Aynı eşik trend tersine kapanış kararında da geçerlidir.</p>
                  <Example label="Örnek">ADX Eşiği = 23.8 → HMA yukarı ama ADX 20 ise sinyal pas geçilir</Example>
                </>
              )}
            </Param>

            <Param name={isEn ? 'ADX Period' : 'ADX Periyodu'} unit="bar" recommended="14" recommendedLabel={rec}>
              <p>{isEn ? 'ADX calculation length. A smaller value produces a more sensitive ADX, a larger value a more stable one.' : 'ADX hesaplama uzunluğu. Küçük değer daha duyarlı, büyük değer daha kararlı ADX üretir.'}</p>
            </Param>
          </Section>

          {/* Durum-tabanlı Giriş/Çıkış */}
          <Section title={isEn ? '📈 State-Based Entry/Exit — Range Bar+HMA+ADX' : '📈 Durum-tabanlı Giriş/Çıkış — Range Bar+HMA+ADX'}>
            <p className="sh-text">
              {isEn ? (
                <><strong>There is no TP</strong> in Range Bar+HMA+ADX — only SL (a safety net, fixed after entry).
                Entry never waits for a flip: whenever flat, it opens instantly in the current HMA direction (including the moment the bot starts).</>
              ) : (
                <>Range Bar+HMA+ADX'te <strong>TP yoktur</strong> — sadece SL (yedek sigorta, giriş sonrası sabit kalır).
                Giriş, flip beklemez: flat kaldığı HER an, o anki HMA yönüne göre hemen açılır (bot başlar başlamaz dahil).</>
              )}
            </p>

            <Timeline steps={isEn ? [
              { label: 'Entry (while flat)', text: 'Opens instantly in whatever direction HMA is pointing (also checking ADX if ADX Threshold>0) — no flip is awaited.', color: '#3b82f6' },
              { label: 'Futures — trend continues', text: 'Position stays open; since there is no TP, it doesn\'t close based on a target.', color: 'var(--accent)' },
              { label: 'Futures — HMA reverses', text: 'The position closes instantly and reopens immediately in the opposite direction on the SAME turn — always in the market.', color: '#f59e0b' },
              { label: 'Spot — HMA turns down', text: 'An open LONG closes; since shorting isn\'t possible it stays flat — no new trade opens until the next upward turn.', color: '#dc2626' },
            ] : [
              { label: 'Giriş (flat iken)', text: 'HMA yönü neyse (ADX Eşiği>0 ise ADX de uygunsa) o yönde hemen açılır — flip beklenmez.', color: '#3b82f6' },
              { label: 'Vadeli — trend devam ediyor', text: 'Pozisyon açık kalır, TP olmadığı için hedefe göre kapanmaz.', color: 'var(--accent)' },
              { label: 'Vadeli — HMA tersine döner', text: 'Pozisyon anında kapatılır ve AYNI turda ters yönde hemen yeniden açılır — sürekli piyasada kalınır.', color: '#f59e0b' },
              { label: 'Spot — HMA aşağı döner', text: 'Açık LONG kapatılır, short açılamadığı için flat kalınır — bir sonraki yukarı dönüşe kadar yeni işlem açılmaz.', color: '#dc2626' },
            ]} />

            <Note>
              {isEn
                ? 'On futures this produces an "always in a position" behavior — trade count is high, and every entry still has to pass the ADX/CHOP/min-RR filters (min-RR is disabled in this strategy since there\'s no TP).'
                : 'Vadelide bu, sürekli piyasada kalan bir "her zaman bir pozisyonda" davranışı üretir — işlem sayısı yüksek olur, her giriş yine ADX/CHOP/min-RR filtrelerinden geçmek zorundadır (min-RR TP olmadığı için bu stratejide devre dışıdır).'}
            </Note>
          </Section>

          {/* Strateji Özeti */}
          <Section title={isEn ? '⚖ Range Bar + HMA + ADX — Summary' : '⚖ Range Bar + HMA + ADX — Özet'}>
            <div className="sh-table-wrap">
              <table className="sh-table">
                <thead>
                  <tr>
                    <th>{isEn ? 'Property' : 'Özellik'}</th>
                    <th>Range Bar + HMA + ADX</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>{isEn ? 'Signal frequency' : 'Sinyal sıklığı'}</td><td>{isEn ? 'State-based, high (always in market on futures)' : 'Durum-tabanlı, yüksek (vadelide sürekli piyasada)'}</td></tr>
                  <tr><td>{isEn ? 'SL behavior' : 'SL davranışı'}</td><td>{isEn ? 'Fixed' : 'Sabit'}</td></tr>
                  <tr><td>{isEn ? 'TP behavior' : 'TP davranışı'}</td><td>{isEn ? 'None (SL only)' : 'Yok (sadece SL)'}</td></tr>
                  <tr><td>{isEn ? 'ADX filter' : 'ADX filtresi'}</td><td>{isEn ? 'Optional — Threshold=0: off, Threshold>0: on' : 'Opsiyonel — Eşik=0: devre dışı, Eşik>0: aktif'}</td></tr>
                  <tr><td>{isEn ? 'Trend-reversal close' : 'Trend tersine kapatma'}</td><td>{isEn ? 'Yes — reopens instantly in the opposite direction on futures, stays flat on spot' : 'Evet — vadelide anında ters yönde yeniden açılır, spot\'ta flat kalınır'}</td></tr>
                </tbody>
              </table>
            </div>
          </Section>

          {/* Analiz Periyodu */}
          <Section title={isEn ? '🕐 Analysis Period' : '🕐 Analiz Periyodu'}>
            <p className="sh-text">
              {isEn
                ? 'Determines which candle interval the bot analyzes signals on. Changeable from the period selector on the chart; locks once the bot is started.'
                : 'Bot sinyallerini hangi mum aralığında analiz edeceğini belirler. Grafik üzerindeki periyot seçicisinden değiştirilebilir; bot başlatılınca kilitlenir.'}
            </p>
            <div className="sh-table-wrap" style={{ marginTop: 8 }}>
              <table className="sh-table">
                <thead><tr><th>{isEn ? 'Period' : 'Periyot'}</th><th>{isEn ? 'Property' : 'Özellik'}</th><th>{isEn ? 'Ideal Use' : 'İdeal Kullanım'}</th></tr></thead>
                <tbody>
                  <tr><td><strong>1m</strong></td><td>{isEn ? 'Very fast signal, high noise' : 'Çok hızlı sinyal, yüksek gürültü'}</td><td>{isEn ? 'Scalping' : 'Scalping'}</td></tr>
                  <tr><td><strong>5m</strong></td><td>{isEn ? 'Fast signal, medium noise' : 'Hızlı sinyal, orta gürültü'}</td><td>{isEn ? 'Intraday (default)' : 'Gün içi (varsayılan)'}</td></tr>
                  <tr><td><strong>1H</strong></td><td>{isEn ? 'Slow signal, low noise' : 'Yavaş sinyal, düşük gürültü'}</td><td>{isEn ? 'Swing trading' : 'Swing trading'}</td></tr>
                  <tr><td><strong>4H</strong></td><td>{isEn ? 'Very slow, very low noise' : 'Çok yavaş, çok az gürültü'}</td><td>{isEn ? 'Medium term' : 'Orta vade'}</td></tr>
                </tbody>
              </table>
            </div>
          </Section>

          {/* SL Cooldown */}
          <Section title="⏸ SL Cooldown">
            <Param name="SL Cooldown" unit={isEn ? 'minutes' : 'dakika'} recommended="5" recommendedLabel={rec}>
              <p>{isEn ? 'No new trade opens on the same pair for this long after a Stop Loss triggers.' : 'Stop Loss tetiklenince aynı paritede bu süre boyunca yeni işlem açılmaz.'}</p>
              <Example label={isEn ? 'Tip' : 'İpucu'}>{isEn ? 'Increase it (15-30 min) during high-volatility periods to prevent chained losses.' : 'Yüksek volatilite dönemlerinde artırın (15-30 dk) zincirleme zararları önleyin.'}</Example>
            </Param>
            <Note>
              {isEn
                ? <><strong>Exception — RSI+SMA / SMA-50:</strong> there is no SL Cooldown field for these 2 strategies — after an SL close, a new trade can open again immediately.</>
                : <><strong>İstisna — RSI+SMA / SMA-50:</strong> bu 2 strateji için SL Cooldown alanı yoktur — SL ile kapanış sonrası yeni işlem hemen açılabilir.</>}
            </Note>
          </Section>

          {/* Özet Tablosu */}
          <Section title={isEn ? '📋 Quick Reference' : '📋 Hızlı Referans'}>
            <div className="sh-table-wrap">
              <table className="sh-table">
                <thead><tr><th>{isEn ? 'Parameter' : 'Parametre'}</th><th>{isEn ? 'Recommended' : 'Önerilen'}</th><th>{isEn ? 'If you decrease it' : 'Düşürürseniz'}</th><th>{isEn ? 'If you increase it' : 'Artırırsanız'}</th></tr></thead>
                <tbody>
                  <tr><td>{isEn ? 'SL Percentage' : 'SL Yüzdesi'}</td><td>0 (ATR)</td><td>{isEn ? 'Tight SL, early-exit risk' : 'Dar SL, erken çıkış riski'}</td><td>{isEn ? 'Wide SL, large-loss risk' : 'Geniş SL, büyük kayıp riski'}</td></tr>
                  <tr><td>{isEn ? 'ATR Period' : 'ATR Periyodu'}</td><td>14</td><td>{isEn ? 'Fast adaptation' : 'Hızlı adaptasyon'}</td><td>{isEn ? 'Stable ATR' : 'Kararlı ATR'}</td></tr>
                  <tr><td>{isEn ? 'SL Multiplier' : 'SL Çarpanı'}</td><td>1.2×</td><td>{isEn ? 'Tight SL, early exit' : 'Dar SL, erken çıkış'}</td><td>{isEn ? 'Wide SL, large loss' : 'Geniş SL, büyük zarar'}</td></tr>
                  <tr><td>SL Cooldown</td><td>{isEn ? '5 min' : '5 dk'}</td><td>{isEn ? 'Fast re-entry' : 'Hızlı geri dönüş'}</td><td>{isEn ? 'Long wait' : 'Uzun bekleme'}</td></tr>
                  <tr><td>{isEn ? 'HMA Period' : 'HMA Periyodu'}</td><td>21</td><td>{isEn ? 'Faster direction change' : 'Daha hızlı yön değişimi'}</td><td>{isEn ? 'Slower, fewer signals' : 'Daha yavaş, daha az sinyal'}</td></tr>
                  <tr><td>{isEn ? 'ADX Period' : 'ADX Periyodu'}</td><td>14</td><td>{isEn ? 'More sensitive ADX' : 'Daha duyarlı ADX'}</td><td>{isEn ? 'Stable ADX' : 'Kararlı ADX'}</td></tr>
                  <tr><td>{isEn ? 'ADX Threshold' : 'ADX Eşiği'}</td><td>0 ({isEn ? 'off' : 'kapalı'})</td><td>{isEn ? 'Only HMA generates signals' : 'Sadece HMA sinyal üretir'}</td><td>{isEn ? 'ADX filter active, fewer signals' : 'ADX filtresi devrede, daha az sinyal'}</td></tr>
                </tbody>
              </table>
            </div>
          </Section>

        </div>

        <div className="sh-footer">
          <button className="btn btn-start" onClick={onClose}>{t('common.understood')}</button>
        </div>

      </div>
    </div>
  );
}
